// Baúl del chofer: lo que cada chofer tiene que devolver al depósito, en dos
// saldos separados (Nico, 16/17/18 y 21-oct):
//
//  · Plata: cobranzas de lo que entregó, envío por envío. El picker recibe el
//    efectivo (la diferencia queda registrada, sin deuda) o verifica la
//    transferencia, y la saca del Baúl.
//  · Paquetes: lo que no entregó (se escanea al recibirlo: reentrega vuelve a
//    estantería; devolución o cancelación queda para la tienda) y lo que trae
//    de vuelta de una entrega (cambio, recupero, «va y vuelve»).
//
// Cada cosa que sale queda como movimiento con quién y cuándo. Un error se
// anula con motivo (db_access), nunca se borra. Nada de esto toca
// liquidaciones.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { subtitleStyle, panelStyle, smallMutedStyle, tableStyle, tdStyle, thStyle, titleStyle } from "../components/ui/pageStyles";
import { useAuth } from "../context/AuthContext";
import { useFlash } from "../hooks/useFlash";
import { driverName } from "../lib/driverName";
import { Req, requestJson } from "../lib/requestJson";
import { ScanRow, useScanQueue } from "../lib/useScanQueue";

type Closure = "REENTREGA" | "DEVOLUCION" | "CANCELACION" | "CAMBIO" | "RECUPERO" | "VA_Y_VUELVE";
type Method = "CASH" | "TRANSFER";
type DriverRow = {
  id: number; username: string; first_name: string | null; last_name: string | null;
  cash_count: number; cash_amount: number; transfer_count: number; transfer_amount: number; parcels: number; items: number;
};
type Ref = { delivery_id: number; tracking: string | number | null; lynx_code: string | null; store: string | null; receiver: string | null };
type CashRow = Ref & {
  rsd_id: number; route_id: number; outcome_at: string; importe_cobranza: number | null; expected_amount: number;
  declared_amount: number | null; declared_difference: number | null; declared_method: Method | null;
};
type ParcelRow = Ref & { route_id: number; since: string; return_destination: string | null; rsd_id: number | null; outcome: string | null; declared_closure: Closure | null; driver_notes: string | null; direccion: string | null };
type ItemRow = Ref & { rsd_id: number; route_id: number; outcome_at: string; closure: Closure; return_item: string };
type RecentRow = Ref & { rsd_id: number; outcome_at: string };
type Movement = {
  id: number; kind: "CASH" | "TRANSFER" | "PARCEL" | "ITEM" | "UNMARK"; delivery_id: number; tracking: string | number | null; lynx_code: string | null;
  expected_amount: number | null; received_amount: number | null; difference: number | null; closure: Closure | null; notes: string | null;
  warehouse_name: string | null; created_by: string; created_at: string; voided_at: string | null; voided_by: string | null; void_reason: string | null;
};
type Detail = {
  start_at: string | null; start_at_day?: string | null;
  driver: { id: number; username: string; first_name: string | null; last_name: string | null };
  totals: Omit<DriverRow, "id" | "username" | "first_name" | "last_name">;
  cash: CashRow[]; parcels: ParcelRow[]; items: ItemRow[]; movements: Movement[]; recent_deliveries: RecentRow[];
};
type ToReturnRow = Ref & { ops_status: string; ops_status_at: string | null; closure: Closure | null; received_by: string | null; warehouse_name: string | null };
type ParcelReceipt = { delivery: Ref & { id: number }; destination: "TIENDA" | "REPARTO"; shelf: { code: string; name: string } | null; alerts: { code: string; text: string }[] };

export const CLOSURE_LABEL: Record<Closure, string> = {
  REENTREGA: "Reentrega", DEVOLUCION: "Devolución", CANCELACION: "Cancelación",
  CAMBIO: "Cambio", RECUPERO: "Recupero", VA_Y_VUELVE: "Va y vuelve",
};
const KIND_LABEL: Record<Movement["kind"], string> = { CASH: "Efectivo", TRANSFER: "Transferencia", PARCEL: "Paquete", ITEM: "Trajo de vuelta", UNMARK: "Se quitó «para la tienda»" };
type LeftOut = { cash: number; cash_amount: number; parcels: number; items: number };

const pesos = (n: number | null | undefined) =>
  n == null ? "—" : new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 2 }).format(n);
const when = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
const day = (s: string | null | undefined) => (s ? new Date(s).toLocaleDateString("es-AR") : "—");
const ref = (r: { tracking: string | number | null; lynx_code: string | null; delivery_id: number }) => String(r.tracking ?? r.lynx_code ?? `#${r.delivery_id}`);
// Desde cuándo cuenta el Baúl: un DATE del servidor. Si la respuesta trae
// `start_at_day` (AAAA-MM-DD) se usa tal cual; si no (un servidor anterior),
// se lee `start_at` como hasta ahora. `start_at` es un TIMESTAMP sin zona:
// con la API en UTC llega como la medianoche UTC y new Date() en Buenos Aires
// lo mostraba (y lo precargaba) como el día anterior.
export type StartAt = { raw: string | null; day: string | null };
export const startAtOf = (o: { start_at?: string | null; start_at_day?: string | null } | null | undefined): StartAt => ({
  raw: o?.start_at ?? null,
  day: typeof o?.start_at_day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(o.start_at_day) ? o.start_at_day : null,
});
const startAtLabel = (s: StartAt) => {
  if (!s.day) return day(s.raw);
  const [y, m, d] = s.day.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("es-AR");
};
const startAtInput = (s: StartAt) => s.day ?? (s.raw ? String(s.raw).slice(0, 10) : "");

// Importe escrito a la argentina: punto de miles y coma decimal («1.500»,
// «1.234,56», «1500,5»). Devuelve null si está vacío y NaN si no se entiende
// (p. ej. «1500.50» o «1.5»): nunca adivina, el picker lo corrige.
export function parseArs(text: string): number | null {
  const s = text.replace(/[\s$]/g, "");
  if (!s) return null;
  if (!/^(\d{1,3}(\.\d{3})+|\d+)(,\d{1,2})?$/.test(s)) return NaN;
  return Number(s.replace(/\./g, "").replace(",", "."));
}
// Al revés, para precargar el campo: sin miles y con coma decimal.
const arsInput = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(".", ","));

/* ===== Estilos (mismo lenguaje que PlantaPage.tsx) ===== */
const sectionTitle: React.CSSProperties = { margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" };
const numStyle: React.CSSProperties = { ...tdStyle, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
const selectStyle: React.CSSProperties = { minHeight: "40px", padding: "0 12px", borderRadius: "12px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)" };
const cellInput: React.CSSProperties = { ...selectStyle, width: "130px", textAlign: "right" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color, whiteSpace: "nowrap" });
const WARN = "var(--warning, #d29922)";
const diffColor = (n: number) => (Math.abs(n) < 0.005 ? "var(--success)" : WARN);

export default function BaulPage() {
  const { token, capabilities } = useAuth();
  const isPicker = capabilities?.profile === "PICKER";
  const canFix = !!capabilities?.db_access;
  const [tab, setTab] = useState<"drivers" | "return">("drivers");
  const [drivers, setDrivers] = useState<DriverRow[] | null>(null);
  const [startAt, setStartAt] = useState<StartAt>({ raw: null, day: null });
  const [selected, setSelected] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, flash] = useFlash(6000);

  const req: Req = useCallback((path, init = {}) => requestJson(path, { ...init, token }), [token]);

  const loadSummary = useCallback(async () => {
    try {
      const json = await req("/v2/baul");
      setDrivers(json.drivers ?? []);
      setStartAt(startAtOf(json));
    } catch (e: any) { setError(e.message); }
  }, [req]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ maxWidth: "760px" }}>
            <h2 style={titleStyle}>Baúl</h2>
            <p style={subtitleStyle}>
              Lo que cada chofer tiene que devolver al depósito: la plata que cobró entregando y los paquetes que no entregó
              o trae de vuelta. El picker lo recibe cosa por cosa y queda registrado quién y cuándo. No descuenta nada de las
              liquidaciones.
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Button variant={tab === "drivers" ? "primary" : "secondary"} onClick={() => setTab("drivers")}>Choferes</Button>
            <Button variant={tab === "return" ? "primary" : "secondary"} onClick={() => setTab("return")}>Para devolver a la tienda</Button>
          </div>
        </div>
        <StartAtLine startAt={startAt} canFix={canFix} req={req} onSaved={(s) => { setStartAt(s); loadSummary(); flash("Fecha de inicio guardada."); }} onError={setError} />
        {!isPicker ? (
          <p style={{ ...smallMutedStyle, margin: "10px 0 0 0" }}>Solo un picker con depósito asignado registra lo que recibe. Acá podés consultar.</p>
        ) : null}
      </Card>

      {error ? <div role="alert" style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)", display: "flex", justifyContent: "space-between", gap: "12px" }}>
        <span>{error}</span><button type="button" onClick={() => setError(null)} style={{ background: "none", border: 0, color: "inherit", cursor: "pointer" }}>✕</button>
      </div> : null}
      {notice ? <div style={{ ...panelStyle, borderColor: "var(--success)", color: "var(--success)" }}>{notice}</div> : null}

      {tab === "return" ? <ToReturnPanel req={req} canFix={canFix} onError={setError} onNotice={flash} /> : selected ? (
        <DriverDetail
          driverId={selected} req={req} isPicker={isPicker} canFix={canFix}
          onBack={() => { setSelected(null); loadSummary(); }} onError={setError} onNotice={flash}
        />
      ) : (
        <DriversTable drivers={drivers} onOpen={(id) => { setError(null); setSelected(id); }} />
      )}
    </div>
  );
}

function StartAtLine({ startAt, canFix, req, onSaved, onError }: { startAt: StartAt; canFix: boolean; req: Req; onSaved: (s: StartAt) => void; onError: (m: string | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  // Correr la fecha hacia adelante esconde lo que siga sin rendir: antes de
  // guardar se pregunta cuánto queda afuera y se pide confirmación.
  const save = async () => {
    try {
      const pre = await req(`/v2/baul/config/preview?start_at=${encodeURIComponent(value)}`);
      const lo: LeftOut = pre.left_out ?? { cash: 0, cash_amount: 0, parcels: 0, items: 0 };
      if (lo.cash || lo.parcels || lo.items) {
        const parts = [
          lo.cash ? `${lo.cash} cobranza${lo.cash > 1 ? "s" : ""} (${pesos(lo.cash_amount)})` : null,
          lo.parcels ? `${lo.parcels} paquete${lo.parcels > 1 ? "s" : ""} sin entregar` : null,
          lo.items ? `${lo.items} cosa${lo.items > 1 ? "s" : ""} para traer` : null,
        ].filter(Boolean).join(", ");
        if (!window.confirm(`Con esa fecha quedan afuera del Baúl, sin rendir: ${parts}. ¿Guardar igual?`)) return;
      }
      const json = await req("/v2/baul/config", { method: "PUT", body: { start_at: value } });
      setEditing(false);
      onSaved(startAtOf(json.config));
    } catch (e: any) { onError(e.message); }
  };
  return (
    <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", marginTop: "12px", fontSize: "14px", color: "var(--text-primary)" }}>
      <span>Cuenta desde el <strong>{startAtLabel(startAt)}</strong>: lo anterior no aparece.</span>
      {canFix && !editing ? <Button variant="ghost" onClick={() => { setValue(startAtInput(startAt)); setEditing(true); }}>Cambiar</Button> : null}
      {editing ? (
        <>
          <input type="date" aria-label="Fecha de inicio del Baúl" style={selectStyle} value={value} onChange={(e) => setValue(e.target.value)} />
          <Button onClick={save} disabled={!value}>Guardar</Button>
          <Button variant="ghost" onClick={() => setEditing(false)}>Cancelar</Button>
        </>
      ) : null}
    </div>
  );
}

function DriversTable({ drivers, onOpen }: { drivers: DriverRow[] | null; onOpen: (id: number) => void }) {
  if (drivers === null) return <Card><div style={smallMutedStyle}>Cargando…</div></Card>;
  if (!drivers.length) return <Card><div style={{ color: "var(--text-secondary)" }}>Ningún chofer tiene nada pendiente en el Baúl.</div></Card>;
  return (
    <Card>
      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Chofer</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Efectivo a rendir</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Transferencias a verificar</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Paquetes sin entregar</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Trae de vuelta</th>
              <th style={thStyle} />
            </tr>
          </thead>
          <tbody>
            {drivers.map((d) => (
              <tr key={d.id}>
                <td style={tdStyle}><strong>{driverName(d)}</strong><div style={smallMutedStyle}>{d.username}</div></td>
                <td style={numStyle}>{d.cash_count ? <>{pesos(d.cash_amount)}<div style={smallMutedStyle}>{d.cash_count} envío{d.cash_count > 1 ? "s" : ""}</div></> : "—"}</td>
                <td style={numStyle}>{d.transfer_count ? <>{pesos(d.transfer_amount)}<div style={smallMutedStyle}>{d.transfer_count} envío{d.transfer_count > 1 ? "s" : ""}</div></> : "—"}</td>
                <td style={numStyle}>{d.parcels || "—"}</td>
                <td style={numStyle}>{d.items || "—"}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}><Button variant="secondary" onClick={() => onOpen(d.id)}>Abrir</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ===== Detalle de un chofer ===== */
function DriverDetail({ driverId, req, isPicker, canFix, onBack, onError, onNotice }: {
  driverId: number; req: Req; isPicker: boolean; canFix: boolean;
  onBack: () => void; onError: (m: string | null) => void; onNotice: (m: string) => void;
}) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const load = useCallback(async () => {
    try { setDetail(await req(`/v2/baul/drivers/${driverId}`)); } catch (e: any) { onError(e.message); }
  }, [req, driverId, onError]);
  useEffect(() => { load(); }, [load]);

  if (!detail) return <Card><div style={smallMutedStyle}>Cargando…</div></Card>;
  const t = detail.totals;
  return (
    <>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <h3 style={{ ...sectionTitle, fontSize: "22px" }}>{driverName(detail.driver)}</h3>
            <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
              Efectivo {pesos(t.cash_amount)} · Transferencias {pesos(t.transfer_amount)} · {t.parcels} paquete{t.parcels === 1 ? "" : "s"} sin entregar · {t.items} cosa{t.items === 1 ? "" : "s"} para traer
            </div>
          </div>
          <Button variant="secondary" onClick={onBack}>← Todos los choferes</Button>
        </div>
      </Card>
      <MoneySection detail={detail} req={req} isPicker={isPicker} onDone={(m) => { onNotice(m); load(); }} onError={onError} />
      <ParcelsSection detail={detail} req={req} isPicker={isPicker} onDone={load} onError={onError} />
      <ItemsSection detail={detail} req={req} isPicker={isPicker} onDone={(m) => { onNotice(m); load(); }} onError={onError} />
      <MovementsSection detail={detail} req={req} canFix={canFix} onDone={(m) => { onNotice(m); load(); }} onError={onError} />
    </>
  );
}

type Draft = { checked: boolean; method: Method; received: string };

function MoneySection({ detail, req, isPicker, onDone, onError }: { detail: Detail; req: Req; isPicker: boolean; onDone: (m: string) => void; onError: (m: string | null) => void }) {
  // Lo recibido se precarga con lo que el chofer dijo que cobró (o, si no
  // dijo nada, con la cobranza); el esperado es siempre la cobranza.
  // Escanear un paquete o «Recibido» recargan el detalle (detail.cash nuevo):
  // lo ya marcado o escrito se conserva por rsd_id; las cobranzas nuevas
  // entran con lo precargado y las que ya no están (rendidas) se van.
  const initial = useCallback((prev: Record<number, Draft> = {}) => Object.fromEntries(detail.cash.map((r) => [r.rsd_id, prev[r.rsd_id] ?? {
    checked: false, method: (r.declared_method ?? "CASH") as Method, received: arsInput(r.declared_amount ?? r.expected_amount),
  }])) as Record<number, Draft>, [detail.cash]);
  const [draft, setDraft] = useState<Record<number, Draft>>(() => initial());
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  useEffect(() => { setDraft((prev) => initial(prev)); }, [initial]);

  const set = (id: number, patch: Partial<Draft>) => setDraft((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  const chosen = detail.cash.filter((r) => draft[r.rsd_id]?.checked);
  const invalid = (d: Draft | undefined) => !!d && d.method === "CASH" && (() => { const n = parseArs(d.received); return n === null || Number.isNaN(n); })();
  const sums = useMemo(() => {
    let expected = 0, received = 0, transfers = 0;
    for (const r of chosen) {
      const d = draft[r.rsd_id];
      if (d.method === "TRANSFER") { transfers += 1; continue; }
      expected += r.expected_amount;
      const n = parseArs(d.received);
      received += n === null || Number.isNaN(n) ? 0 : n;
    }
    return { expected, received, diff: received - expected, transfers };
  }, [chosen, draft]);
  const allChecked = detail.cash.length > 0 && chosen.length === detail.cash.length;

  const submit = async () => {
    if (sending.current) return;
    const bad = chosen.filter((r) => invalid(draft[r.rsd_id]));
    if (bad.length) {
      onError(`Revisá lo recibido en ${bad.map(ref).join(", ")}: escribí el importe con punto de miles y coma decimal (ej. 1.500 o 1.234,56).`);
      return;
    }
    sending.current = true;
    setBusy(true); onError(null);
    try {
      const items = chosen.map((r) => {
        const d = draft[r.rsd_id];
        return d.method === "TRANSFER" ? { rsd_id: r.rsd_id, method: "TRANSFER" } : { rsd_id: r.rsd_id, method: "CASH", received_amount: parseArs(d.received) };
      });
      const json = await req(`/v2/baul/drivers/${detail.driver.id}/cash`, { method: "POST", body: { items } });
      const tt = json.totals;
      const parts = [];
      if (tt.cash_expected || tt.cash_received) parts.push(`efectivo ${pesos(tt.cash_received)} de ${pesos(tt.cash_expected)}${Math.abs(tt.difference) >= 0.005 ? ` (diferencia ${pesos(tt.difference)}, queda registrada)` : ""}`);
      if (tt.transfers) parts.push(`${tt.transfers} transferencia${tt.transfers > 1 ? "s" : ""} verificada${tt.transfers > 1 ? "s" : ""}`);
      onDone(`Rendición registrada: ${parts.join(" · ")}.`);
    } catch (e: any) { onError(e.message); }
    finally { sending.current = false; setBusy(false); }
  };

  return (
    <Card>
      <h3 style={sectionTitle}>Plata</h3>
      <p style={{ ...smallMutedStyle, margin: "6px 0 12px 0" }}>
        Cobranzas de lo que entregó. Marcá lo que te rinde: en efectivo poné lo que recibiste; una transferencia se marca cuando verificaste que entró.
      </p>
      {!detail.cash.length ? <div style={{ color: "var(--text-secondary)" }}>No tiene cobranzas pendientes.</div> : (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}><input type="checkbox" aria-label="Marcar todas" checked={allChecked} disabled={!isPicker} onChange={(e) => setDraft((d) => Object.fromEntries(Object.entries(d).map(([k, v]) => [k, { ...v, checked: e.target.checked }])))} /></th>
                  <th style={thStyle}>Entregado</th>
                  <th style={thStyle}>Envío</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>A cobrar (esperado)</th>
                  <th style={thStyle}>Cómo pagó</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Recibido</th>
                </tr>
              </thead>
              <tbody>
                {detail.cash.map((r) => {
                  const d = draft[r.rsd_id] ?? { checked: false, method: "CASH", received: "" };
                  return (
                    <tr key={r.rsd_id} data-testid={`cash-${r.delivery_id}`}>
                      <td style={tdStyle}><input type="checkbox" aria-label={`Rendir ${ref(r)}`} checked={d.checked} disabled={!isPicker} onChange={(e) => set(r.rsd_id, { checked: e.target.checked })} /></td>
                      <td style={tdStyle}>{when(r.outcome_at)}</td>
                      <td style={tdStyle}><span style={{ fontFamily: "monospace" }}>{ref(r)}</span><div style={smallMutedStyle}>{r.store ?? "—"} · {r.receiver ?? "—"}</div></td>
                      <td style={numStyle}>
                        {pesos(r.expected_amount)}
                        {r.declared_amount != null ? (
                          <div data-testid={`declared-${r.delivery_id}`} style={smallMutedStyle}>
                            el chofer declaró {pesos(r.declared_amount)}
                            {r.declared_difference != null && Math.abs(r.declared_difference) >= 0.005
                              ? <span style={{ color: WARN, fontWeight: 600 }}> (dif. {pesos(r.declared_difference)})</span> : null}
                          </div>
                        ) : null}
                      </td>
                      <td style={tdStyle}>
                        <select aria-label={`Cómo pagó ${ref(r)}`} style={selectStyle} value={d.method} disabled={!isPicker} onChange={(e) => set(r.rsd_id, { method: e.target.value as Method })}>
                          <option value="CASH">Efectivo</option>
                          <option value="TRANSFER">Transferencia</option>
                        </select>
                        {r.declared_method ? <div style={smallMutedStyle}>el chofer dijo {r.declared_method === "TRANSFER" ? "transferencia" : "efectivo"}</div> : null}
                      </td>
                      <td style={numStyle}>
                        {d.method === "CASH" ? (
                          <>
                            <input aria-label={`Recibido ${ref(r)}`} aria-invalid={invalid(d)} style={invalid(d) ? { ...cellInput, borderColor: "var(--danger)" } : cellInput} inputMode="decimal" value={d.received} disabled={!isPicker} onChange={(e) => set(r.rsd_id, { received: e.target.value })} />
                            {invalid(d) ? <div style={{ fontSize: "12px", color: "var(--danger)" }}>importe inválido</div> : null}
                          </>
                        ) : <span style={smallMutedStyle}>verificada</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "center", marginTop: "14px" }}>
            <div style={{ fontSize: "14px", color: "var(--text-primary)" }}>
              {chosen.length ? (
                <>
                  Efectivo: esperado <strong>{pesos(sums.expected)}</strong>, recibido <strong>{pesos(sums.received)}</strong>
                  {" · "}<span style={{ color: diffColor(sums.diff), fontWeight: 700 }}>diferencia {pesos(sums.diff)}</span>
                  {sums.transfers ? ` · ${sums.transfers} transferencia${sums.transfers > 1 ? "s" : ""}` : ""}
                </>
              ) : <span style={smallMutedStyle}>Marcá las cobranzas que rinde.</span>}
            </div>
            <Button onClick={submit} disabled={!isPicker || busy || !chosen.length}>{busy ? "…" : `Registrar rendición (${chosen.length})`}</Button>
          </div>
          {chosen.length && Math.abs(sums.diff) >= 0.005 ? (
            <p style={{ ...smallMutedStyle, margin: "8px 0 0 0" }}>La diferencia queda registrada en el movimiento; no genera deuda ni descuento.</p>
          ) : null}
        </>
      )}
    </Card>
  );
}

function ParcelsSection({ detail, req, isPicker, onDone, onError }: { detail: Detail; req: Req; isPicker: boolean; onDone: () => void; onError: (m: string | null) => void }) {
  const [dest, setDest] = useState<"" | Closure>("");
  const [last, setLast] = useState<ParcelReceipt | null>(null);
  const focusScan = () => document.getElementById("baul-scan")?.focus();

  // Cola de escaneo (useScanQueue): el campo se vacía en el acto y el paquete
  // siguiente se puede escanear sin esperar. El destino se toma al encolar
  // (cambiarlo después no toca lo que ya está en la cola) y el detalle se
  // recarga una sola vez, cuando la cola se vacía.
  const scan = useScanQueue<Record<string, unknown>, ParcelReceipt>(
    (body) => req(`/v2/baul/drivers/${detail.driver.id}/parcels`, { method: "POST", body }),
    {
      onResult: (r) => { setLast(r); onError(null); focusScan(); },
      onError: (m, code) => { onError(`${code}: ${m}`); focusScan(); },
      onIdle: onDone,
      // Lo que quede sin respuesta vuelve a la lista como «Sin confirmar».
      persistKey: `scan:baul:${detail.driver.id}`,
    },
  );
  const scanCode = () => { scan.enter((code) => ({ qr: code, destination: dest || undefined })); focusScan(); };
  // «Recibir sin escanear» no se suma a la cola mientras haya algo en curso:
  // un doble clic mandaría el mismo paquete dos veces.
  const receiveRow = (p: ParcelRow) => {
    if (scan.isBusy()) return;
    scan.push(ref(p), { delivery_id: p.delivery_id, destination: dest || undefined });
  };

  return (
    <Card>
      <h3 style={sectionTitle}>Paquetes sin entregar</h3>
      <p style={{ ...smallMutedStyle, margin: "6px 0 12px 0" }}>
        Escaneá cada paquete que devuelve. Si se reentrega, vuelve a su estantería; si es una devolución o una cancelación, queda
        separado para devolver a la tienda y no sale a reparto.
      </p>
      {isPicker ? (
        <div style={{ display: "flex", gap: "10px", alignItems: "end", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 320px" }}>
            <Input
              id="baul-scan" label="Escaneá la etiqueta o escribí el tracking" value={scan.value} placeholder="QR de ML o de LightData, LP000000123 o el tracking"
              onChange={(e: any) => scan.setValue(e.target.value)}
              onKeyDown={(e: any) => { if (e.key === "Enter") scanCode(); }}
            />
          </div>
          <select aria-label="Destino" style={{ ...selectStyle, minHeight: "48px" }} value={dest} onChange={(e) => setDest(e.target.value as "" | Closure)}>
            <option value="">Lo que declaró el chofer (si no, reentrega)</option>
            <option value="REENTREGA">Reentrega</option>
            <option value="DEVOLUCION">Devolución a la tienda</option>
            <option value="CANCELACION">Cancelado (vuelve a la tienda)</option>
          </select>
          <Button onClick={scanCode} disabled={!scan.value.trim()}>Recibir</Button>
        </div>
      ) : null}

      {last ? (
        <div data-testid="parcel-receipt" style={{ ...panelStyle, marginTop: "14px", borderColor: last.destination === "TIENDA" ? WARN : "var(--success)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "baseline" }}>
            <div style={{ fontSize: "36px", fontWeight: 800, lineHeight: 1.1, fontFamily: "monospace", color: last.destination === "TIENDA" ? WARN : "var(--success)" }}>
              {last.destination === "TIENDA" ? "PARA LA TIENDA" : last.shelf?.code ?? "SIN ESTANTERÍA"}
            </div>
            <div style={{ textAlign: "right", fontSize: "14px", color: "var(--text-primary)" }}>
              <div style={{ fontFamily: "monospace", fontWeight: 700 }}>{last.delivery.tracking ?? last.delivery.lynx_code ?? last.delivery.id}</div>
              <div>{last.delivery.store ?? "—"}</div>
            </div>
          </div>
          <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
            {last.destination === "TIENDA" ? "Separalo: se devuelve a la tienda." : last.shelf ? `Va a ${last.shelf.name}.` : ""}
            {last.alerts.map((a) => ` ${a.text}`).join("")}
          </div>
        </div>
      ) : null}

      {scan.rows.length || scan.pending ? (
        <div style={{ marginTop: "14px" }}>
          <div style={{ ...smallMutedStyle, marginBottom: "6px", fontWeight: 600 }}>
            Escaneados en esta sesión · {scan.rows.length}{scan.pending ? ` · En cola: ${scan.pending}` : ""}
          </div>
          <div style={{ maxHeight: "240px", overflowY: "auto" }}>
            {scan.rows.map((row) => <ScanLine key={row.key} row={row} />)}
          </div>
        </div>
      ) : null}

      {!detail.parcels.length ? <div style={{ color: "var(--text-secondary)", marginTop: "14px" }}>No tiene paquetes sin entregar.</div> : (
        <div style={{ overflowX: "auto", marginTop: "14px" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Desde</th>
                <th style={thStyle}>Envío</th>
                <th style={thStyle}>Qué declaró</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {detail.parcels.map((p) => (
                <tr key={p.delivery_id} data-testid={`parcel-${p.delivery_id}`}>
                  <td style={tdStyle}>{when(p.since)}<div style={smallMutedStyle}>recorrido #{p.route_id}</div></td>
                  <td style={tdStyle}><span style={{ fontFamily: "monospace" }}>{ref(p)}</span><div style={smallMutedStyle}>{p.store ?? "—"} · {p.receiver ?? "—"}</div></td>
                  <td style={tdStyle}>
                    {p.declared_closure ? <span style={badge(p.declared_closure === "REENTREGA" ? "var(--success)" : WARN)}>{CLOSURE_LABEL[p.declared_closure]}</span>
                      : <span style={smallMutedStyle}>{p.outcome ? "no entregado, sin destino" : "recorrido cancelado"}</span>}
                    {p.driver_notes ? <div style={smallMutedStyle}>«{p.driver_notes}»</div> : null}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    {isPicker ? <Button variant="secondary" disabled={scan.busy} onClick={() => receiveRow(p)}>Recibir sin escanear</Button> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// Una línea de «Escaneados en esta sesión». Si falló, va en rojo con el
// código tal como se escaneó, porque el campo ya se vació.
function ScanLine({ row }: { row: ScanRow<ParcelReceipt> }) {
  const r = row.result;
  return (
    <div data-testid="baul-scan-row" style={{ display: "flex", gap: "10px", padding: "6px 0", borderBottom: "1px solid var(--border)", alignItems: "baseline", fontSize: "13px", color: r ? "var(--text-primary)" : "var(--danger)" }}>
      <span style={{ ...smallMutedStyle, width: "46px", flexShrink: 0 }}>{row.at.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}</span>
      {r ? (
        <>
          <span style={{ fontFamily: "monospace" }}>{r.delivery.tracking ?? r.delivery.lynx_code ?? r.delivery.id}</span>
          <span style={{ fontWeight: 700, color: r.destination === "TIENDA" ? WARN : "var(--text-primary)" }}>{r.destination === "TIENDA" ? "Para la tienda" : r.shelf?.code ?? "Sin estantería"}</span>
          {r.alerts.length ? <span style={smallMutedStyle}>{r.alerts.length} aviso{r.alerts.length > 1 ? "s" : ""}</span> : null}
        </>
      ) : (
        <>
          <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{row.code}</span>
          <span>{row.error}</span>
        </>
      )}
    </div>
  );
}

function ItemsSection({ detail, req, isPicker, onDone, onError }: { detail: Detail; req: Req; isPicker: boolean; onDone: (m: string) => void; onError: (m: string | null) => void }) {
  const [busy, setBusy] = useState(false);
  const [rsd, setRsd] = useState("");
  const [kind, setKind] = useState<Closure>("CAMBIO");
  const [what, setWhat] = useState("");
  const sending = useRef(false);

  const post = async (body: Record<string, unknown>, msg: string) => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true); onError(null);
    try {
      await req(`/v2/baul/drivers/${detail.driver.id}/items`, { method: "POST", body });
      setRsd(""); setWhat("");
      onDone(msg);
    } catch (e: any) { onError(e.message); }
    finally { sending.current = false; setBusy(false); }
  };

  return (
    <Card>
      <h3 style={sectionTitle}>Trae de vuelta</h3>
      <p style={{ ...smallMutedStyle, margin: "6px 0 12px 0" }}>
        Lo que el chofer trae de una entrega: un cambio, un recupero o cualquier envío que «va y vuelve».
      </p>
      {!detail.items.length ? <div style={{ color: "var(--text-secondary)" }}>No declaró nada para traer.</div> : (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Entregado</th><th style={thStyle}>Envío</th><th style={thStyle}>Qué trae</th><th style={thStyle} /></tr></thead>
            <tbody>
              {detail.items.map((i) => (
                <tr key={i.rsd_id}>
                  <td style={tdStyle}>{when(i.outcome_at)}</td>
                  <td style={tdStyle}><span style={{ fontFamily: "monospace" }}>{ref(i)}</span><div style={smallMutedStyle}>{i.store ?? "—"} · {i.receiver ?? "—"}</div></td>
                  <td style={tdStyle}><span style={badge(WARN)}>{CLOSURE_LABEL[i.closure]}</span> {i.return_item}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    {isPicker ? <Button variant="secondary" disabled={busy} onClick={() => post({ rsd_id: i.rsd_id }, `Recibido: ${i.return_item}.`)}>Recibido</Button> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isPicker ? (
        <div style={{ ...panelStyle, marginTop: "14px" }}>
          <div style={{ ...smallMutedStyle, fontWeight: 600, marginBottom: "8px" }}>Registrar algo que trajo y no declaró</div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
            <select aria-label="Entrega" style={{ ...selectStyle, flex: "1 1 260px" }} value={rsd} onChange={(e) => setRsd(e.target.value)}>
              <option value="">Elegí la entrega (últimos 7 días)</option>
              {detail.recent_deliveries.map((r) => (
                <option key={r.rsd_id} value={r.rsd_id}>{day(r.outcome_at)} · {ref(r)} · {r.store ?? "—"} · {r.receiver ?? "—"}</option>
              ))}
            </select>
            <select aria-label="Tipo" style={selectStyle} value={kind} onChange={(e) => setKind(e.target.value as Closure)}>
              <option value="CAMBIO">Cambio</option>
              <option value="RECUPERO">Recupero</option>
              <option value="VA_Y_VUELVE">Va y vuelve</option>
            </select>
            <input aria-label="Qué trajo" style={{ ...selectStyle, flex: "1 1 220px" }} placeholder="Qué trajo (ej. zapatillas talle 40)" value={what} onChange={(e) => setWhat(e.target.value)} />
            <Button disabled={busy || !rsd || !what.trim()} onClick={() => post({ rsd_id: Number(rsd), kind, description: what.trim() }, `Recibido: ${what.trim()}.`)}>Registrar</Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function MovementsSection({ detail, req, canFix, onDone, onError }: { detail: Detail; req: Req; canFix: boolean; onDone: (m: string) => void; onError: (m: string | null) => void }) {
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  const voidIt = async (m: Movement) => {
    if (sending.current) return;
    const reason = window.prompt(`Anular ${KIND_LABEL[m.kind].toLowerCase()} de ${ref(m)}. ¿Por qué?`);
    if (!reason || !reason.trim()) return;
    sending.current = true;
    setBusy(true);
    try {
      await req(`/v2/baul/movements/${m.id}/void`, { method: "POST", body: { reason: reason.trim() } });
      onDone("Movimiento anulado: vuelve al Baúl.");
    } catch (e: any) { onError(e.message); }
    finally { sending.current = false; setBusy(false); }
  };
  return (
    <Card>
      <h3 style={sectionTitle}>Movimientos (últimos 60 días)</h3>
      {!detail.movements.length ? <div style={{ color: "var(--text-secondary)", marginTop: "10px" }}>Todavía no se recibió nada de este chofer.</div> : (
        <div style={{ overflowX: "auto", marginTop: "10px" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Cuándo</th><th style={thStyle}>Qué</th><th style={thStyle}>Envío</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Esperado</th><th style={{ ...thStyle, textAlign: "right" }}>Recibido</th>
                <th style={thStyle}>Detalle</th><th style={thStyle}>Quién</th><th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {detail.movements.map((m) => {
                const money = m.kind === "CASH" || m.kind === "TRANSFER";
                return (
                  <tr key={m.id} style={m.voided_at ? { opacity: 0.55 } : undefined}>
                    <td style={tdStyle}>{when(m.created_at)}</td>
                    <td style={tdStyle}>{KIND_LABEL[m.kind]}</td>
                    <td style={{ ...tdStyle, fontFamily: "monospace" }}>{ref(m)}</td>
                    <td style={numStyle}>{money ? pesos(m.expected_amount) : ""}</td>
                    <td style={numStyle}>
                      {money ? pesos(m.received_amount) : ""}
                      {money && m.difference != null && Math.abs(m.difference) >= 0.005 ? <div style={{ fontSize: "12px", color: WARN }}>dif. {pesos(m.difference)}</div> : null}
                    </td>
                    <td style={tdStyle}>
                      {m.closure ? CLOSURE_LABEL[m.closure] : ""}{m.closure && m.notes ? " · " : ""}{m.notes ?? ""}
                      {m.voided_at ? <div style={{ fontSize: "12px", color: "var(--danger)" }}>Anulado por {m.voided_by} ({when(m.voided_at)}): {m.void_reason}</div> : null}
                    </td>
                    <td style={tdStyle}>{m.created_by}{m.warehouse_name ? <div style={smallMutedStyle}>{m.warehouse_name}</div> : null}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {canFix && !m.voided_at && m.kind !== "PARCEL" && m.kind !== "UNMARK" ? <Button variant="ghost" disabled={busy} onClick={() => voidIt(m)}>Anular</Button> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ToReturnPanel({ req, canFix, onError, onNotice }: { req: Req; canFix: boolean; onError: (m: string | null) => void; onNotice: (m: string) => void }) {
  const [rows, setRows] = useState<ToReturnRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  const load = useCallback(() => {
    req("/v2/baul/to-return").then((j) => setRows(j.deliveries ?? [])).catch((e) => onError(e.message));
  }, [req, onError]);
  useEffect(() => { load(); }, [load]);
  // Corrección (db_access): el envío quedó marcado por error y no tiene salida.
  const clear = async (r: ToReturnRow) => {
    if (sending.current) return;
    const reason = window.prompt(`Quitar la marca «para la tienda» de ${ref(r)}: vuelve a poder salir a reparto. ¿Por qué?`);
    if (!reason || !reason.trim()) return;
    sending.current = true;
    setBusy(true); onError(null);
    try {
      await req(`/v2/baul/to-return/${r.delivery_id}/clear`, { method: "POST", body: { reason: reason.trim() } });
      onNotice(`Se quitó la marca de ${ref(r)}: ya puede salir a reparto.`);
      load();
    } catch (e: any) { onError(e.message); }
    finally { sending.current = false; setBusy(false); }
  };
  return (
    <Card>
      <h3 style={sectionTitle}>Para devolver a la tienda</h3>
      <p style={{ ...smallMutedStyle, margin: "6px 0 12px 0" }}>
        Paquetes que volvieron por devolución o cancelación y siguen en el depósito. No salen a reparto. La devolución a la tienda
        se resuelve aparte (módulo de Devoluciones).
      </p>
      {rows === null ? <div style={smallMutedStyle}>Cargando…</div> : !rows.length ? <div style={{ color: "var(--text-secondary)" }}>No hay nada para devolver.</div> : (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Tienda</th><th style={thStyle}>Envío</th><th style={thStyle}>Por qué</th><th style={thStyle}>Recibió</th><th style={thStyle}>Desde</th>{canFix ? <th style={thStyle} /> : null}</tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.delivery_id}>
                  <td style={tdStyle}><strong>{r.store ?? "—"}</strong></td>
                  <td style={tdStyle}><span style={{ fontFamily: "monospace" }}>{ref(r)}</span><div style={smallMutedStyle}>{r.receiver ?? "—"}</div></td>
                  <td style={tdStyle}>{r.closure ? CLOSURE_LABEL[r.closure] : "—"}</td>
                  <td style={tdStyle}>{r.received_by ?? "—"}{r.warehouse_name ? <div style={smallMutedStyle}>{r.warehouse_name}</div> : null}</td>
                  <td style={tdStyle}>{when(r.ops_status_at)}</td>
                  {canFix ? (
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <Button variant="ghost" disabled={busy} onClick={() => clear(r)}>Quitar marca</Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
